import PowersportsAsset from '../../asset/powersports-asset';
import PowersportsCta from '../../cta/powersports-cta';
import Cta from '../../cta';
import Platform, { PageTypes, PlatformAssetWrapperMap } from '../index';
// CSS Style files content - Each imported variable is holding the content of the file as a string
import AssetWrapper from '../../asset/wrappers';
import { WrappedHTMLElement } from '../../util/parse-data';
import { AssetHTMLElement } from '../../typings';
import { PageNotReady } from '../../errors';
import srpStyle from './style/srp.css';
import { StyleTag, createStyleTag } from '../../util/create-style-tag';
import { OlWarning } from '../../events';

/**
 * pixelmotion Base Search Results Page (SRP) Wrapper
 */
abstract class PixelMotionBaseSrpAssetWrapper extends AssetWrapper<PowersportsAsset> {
  getDocumentStyles(): StyleTag[] {
    return [createStyleTag(srpStyle.toString(), 'srp-style')];
  }

  extractDataFromUrl(url: string | null, index: number): string | null {
    if (!url) return null;
    const formattedUrl = new URL(url);
    const vehicleURLData = formattedUrl.pathname.split('/');
    return vehicleURLData[index] || null;
  }

  extractDataFromTitle(text: string): [string, string, string, string] | null {
    if (!text) return null;
    const parts = text.split(' ');
    const condition = parts[0].toLowerCase();
    const year = parts[1];
    const make = parts[2];
    const model = parts.slice(3).join(' ');
    return [condition, year, make, model];
  }

  parseAssetData(): PowersportsAsset {
    const originalPrice =
      new WrappedHTMLElement(this.container, '.msrp-price .price-item-value')
        .getTextContent()
        ?.matchOne(/\$[0-9,]+/)
        ?.toInt() ??
      new WrappedHTMLElement(this.container, '.base-price .price-item-value')
        .getTextContent()
        ?.matchOne(/\$[0-9,]+/)
        ?.toInt() ??
      null;
    const price =
      new WrappedHTMLElement(this.container, '.final-price .price-item-value')
        .getTextContent()
        ?.matchOne(/\$[0-9,]+/)
        ?.toInt() ??
      new WrappedHTMLElement(this.container, '.base-price .price-item-value')
        .getTextContent()
        ?.matchOne(/\$[0-9,]+/)
        ?.toInt() ??
      null;
    const getColor = (colorText: string | null | undefined): string | null | undefined => {
      return colorText ? colorText.split(':')[1].trim() : null;
    };

    const titleData = this.extractDataFromTitle(
      this.container.querySelector('.col-xs-12.grid-title-column.title-column')?.textContent?.trim() || ''
    );

    const asset = new PowersportsAsset({
      category: null,
      make: titleData ? titleData[2] : null,
      model: titleData ? titleData[3] : null,
      id: null,
      year: titleData ? titleData[1] : null,
      condition: titleData ? titleData[0] : null,
      stockNumber: this.container.querySelector('.vlp-item-stockNum span')?.textContent,
      color: getColor(this.container.querySelector('.vlp-item-color-exterior')?.textContent as string),
      vin: this.container.querySelector('.vlp-item-vin span')?.textContent,
      location: this.container.querySelector('.vlp-item-lotName')?.textContent?.replace('Location:', '').trim(),
      price,
      on_sale: !!document.querySelector('.msrp-price') || !!document.querySelector('.final-price'),
      originalPrice,
      subcategory: null
    });

    return asset;
  }

  createCta(): Cta {
    return new PowersportsCta(this, {
      style: 'standard'
    });
  }

  static getPageContainer(): HTMLElement {
    const pageContainer = document.querySelector<HTMLElement>('#inventory-display-container');
    if (pageContainer) return pageContainer;
    else throw new Error('Page container not found.');
  }

  static shouldWatchElements(): boolean {
    return true;
  }
}

/**
 * pixelmotion Search Results Page (SRP) Wrapper
 */
export class PixelMotionSrpAssetWrapper extends PixelMotionBaseSrpAssetWrapper {
  getRenderLocation(): HTMLElement {
    return this.prependRenderLocationBySelector('.cta-button-group');
  }
}

/**
 * pixelmotion Legacy Search Results Page (SRP) Wrapper
 */
export class PixelMotionSrpLegacyAssetWrapper extends PixelMotionBaseSrpAssetWrapper {
  getRenderLocation(): HTMLElement {
    return this.prependRenderLocationBySelector('.vlpm3MainCTA');
  }
}

/**
 * pixelmotion Vehicle Detail Page (VDP) Wrapper
 */
export class PixelMotionVdpAssetWrapper extends AssetWrapper<PowersportsAsset> {
  getRenderLocation(): HTMLElement {
    return this.prependRenderLocationBySelector('#cta-buttons-left');
  }

  extractDataFromUrl(url: string | null, index: number): string | null {
    if (!url) {
      return null;
    }
    const formattedUrl = new URL(url);
    const vehicleURLData = formattedUrl.pathname.split('/');

    return vehicleURLData[index] || null;
  }

  parseAssetData(): PowersportsAsset {
    // `originalPrice` captures either `.msrp-price` or `.base-price`
    const originalPrice =
      new WrappedHTMLElement(this.container, '.msrp-price .pull-right')
        .getTextContent()
        ?.matchOne(/\$[0-9,]+/)
        ?.toInt() ??
      new WrappedHTMLElement(this.container, '.base-price .pull-right')
        .getTextContent()
        ?.matchOne(/\$[0-9,]+/)
        ?.toInt() ??
      new WrappedHTMLElement(this.container, '.base-price .mark3-price-value')
        .getTextContent()
        ?.matchOne(/\$[0-9,]+/)
        ?.toInt() ??
      null;

    // `price` now captures either `.base-price` or `.final-price`
    const price =
      new WrappedHTMLElement(this.container, '.final-price .pull-right')
        .getTextContent()
        ?.matchOne(/\$[0-9,]+/)
        ?.toInt() ??
      new WrappedHTMLElement(this.container, '.final-price .mark3-price-value')
        .getTextContent()
        ?.matchOne(/\$[0-9,]+/)
        ?.toInt() ??
      new WrappedHTMLElement(this.container, '.base-price .pull-right')
        .getTextContent()
        ?.matchOne(/\$[0-9,]+/)
        ?.toInt() ??
      new WrappedHTMLElement(this.container, '.base-price .mark3-price-value')
        .getTextContent()
        ?.matchOne(/\$[0-9,]+/)
        ?.toInt() ??
      null;

    const model = this.extractDataFromUrl(window.location.href, 6);
    const condition = this.extractDataFromUrl(window.location.href, 3);
    const make = this.extractDataFromUrl(window.location.href, 5);
    const year = this.extractDataFromUrl(window.location.href, 4);

    return new PowersportsAsset({
      category: null, // not available on page
      // Data from url tags
      make,
      model,
      id: null, //not available
      year,
      condition,
      // Data from Details table
      stockNumber:
        (this.container.querySelector('.vdp-stockNum span')?.textContent ??
          this.container
            .querySelector('.vdp-stockNum')
            ?.textContent?.replace(/Stock #:\s*/, '')
            .trim()) ||
        '',
      color:
        (this.container.querySelector('.vdp-item-color-exterior-name span')?.textContent ??
          this.container
            .querySelector('.vdp-item-color-exterior-name')
            ?.textContent?.replace('Exterior Color: ', '')
            .trim()) ||
        '',
      vin:
        (this.container.querySelector('.vdp-vin span')?.textContent ??
          this.container.querySelector('.vdp-vin')?.textContent?.replace('VIN: ', '').trim()) ||
        this.extractDataFromUrl(window.location.href, 7) ||
        '',
      location: this.container.querySelector('.vdp-item-lotName span')?.textContent,
      price,
      on_sale: !!(document.querySelector('.msrp-price') || document.querySelector('.mark3-price-item.final-price')),
      originalPrice,
      subcategory: null // not available on page
    });
  }

  createCta(): Cta {
    return new PowersportsCta(this, {
      style: 'standard'
    });
  }

  static getPageContainer(): HTMLElement {
    const selector = '#inventory-display-container';
    const container = document.querySelector<HTMLElement>(selector);
    if (!container) throw new Error(`Page container not found: ${selector}`);
    return container;
  }

  static shouldWatchElements(): boolean {
    return true;
  }
}

/**
 * pixelmotion platform class
 */
export default class PixelMotionPlatform extends Platform {
  pageTypeMap: PlatformAssetWrapperMap = {
    [PageTypes.SRP]: PixelMotionSrpAssetWrapper,
    [PageTypes.SRP_LEGACY]: PixelMotionSrpLegacyAssetWrapper,
    [PageTypes.VDP]: PixelMotionVdpAssetWrapper
  };

  private srpContentObserver: MutationObserver | null = null;
  private observedSrpContainer: Element | null = null;
  private srpRenderPending = false;
  private srpRenderQueue: Promise<void> | null = null;

  watchElements() {
    // Still observe page with mutation observer
    super.watchElements();

    let srpContainer: Element | null = null;
    if (this.pageType === PageTypes.SRP) {
      srpContainer = document.querySelector('#vlp-list');
    } else if (this.pageType === PageTypes.SRP_LEGACY) {
      srpContainer = document.querySelector('#vehicle-listing-container');
    }

    if (!srpContainer) {
      this.disconnectSrpContentObserver();
      return;
    }

    if (this.srpContentObserver && this.observedSrpContainer === srpContainer) return;

    this.disconnectSrpContentObserver();
    this.srpContentObserver = new MutationObserver((mutations) => {
      if (!this.mutationsAddSrpItems(mutations)) return;
      this.queueSrpRender();
    });
    this.observedSrpContainer = srpContainer;

    try {
      this.srpContentObserver.observe(srpContainer, { subtree: true, childList: true });
    } catch (error) {
      this.disconnectSrpContentObserver();
      this.eventBus.dispatchEvent(
        new OlWarning({ detail: { error, msg: '[PixelMotion] Failed to setup renderingComplete handler' } })
      );
    }
  }

  private disconnectSrpContentObserver() {
    this.srpContentObserver?.disconnect();
    this.srpContentObserver = null;
    this.observedSrpContainer = null;
  }

  private getSrpItemSelector(): string | null {
    if (this._pageType === PageTypes.SRP) return '.panel.vlp-item';
    if (this._pageType === PageTypes.SRP_LEGACY) return '.vlpm3VehicleRow';
    return null;
  }

  private mutationsAddSrpItems(mutations: MutationRecord[]): boolean {
    const itemSelector = this.getSrpItemSelector();
    if (!itemSelector) return false;

    return mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) => node instanceof Element && (node.matches(itemSelector) || node.querySelector(itemSelector) !== null)
      )
    );
  }

  private queueSrpRender() {
    this.srpRenderPending = true;
    if (this.srpRenderQueue) return;

    this.srpRenderQueue = this.flushSrpRenderQueue();
  }

  private async flushSrpRenderQueue(): Promise<void> {
    try {
      while (this.srpRenderPending && this.getSrpItemSelector()) {
        this.srpRenderPending = false;

        while (this._renderLock && this.getSrpItemSelector()) {
          await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
        }

        if (!this.getSrpItemSelector()) return;
        if (!this.hasUnwrappedSrpItems()) continue;
        await this._render();
      }
    } finally {
      this.srpRenderQueue = null;
      if (this.srpRenderPending && this.getSrpItemSelector()) this.queueSrpRender();
    }
  }

  private hasUnwrappedSrpItems(): boolean {
    const itemSelector = this.getSrpItemSelector();
    if (!this.observedSrpContainer || !itemSelector) return false;

    return Array.from(this.observedSrpContainer.querySelectorAll<AssetHTMLElement>(itemSelector)).some(
      (item) => !item.olAssetWrapper
    );
  }

  determinePageType(): PageTypes {
    if (window.document.querySelector('#vlp-list')) {
      return PageTypes.SRP;
    }
    if (window.document.querySelector('#vehicle-listing-container')) {
      return PageTypes.SRP_LEGACY;
    }
    if (window.document.querySelector('.vdpview')) {
      return PageTypes.VDP;
    }
    throw new PageNotReady('Render location not found in the container.');
  }

  getAssetContainers(): HTMLElement[] {
    let containers;
    if (this.pageType === PageTypes.SRP) {
      containers = Array.from<HTMLElement>(window.document.querySelectorAll('.panel.vlp-item'));
    } else if (this.pageType === PageTypes.SRP_LEGACY) {
      containers = Array.from<HTMLElement>(window.document.querySelectorAll('.vlpm3VehicleRow'));
    } else if (this.pageType === PageTypes.VDP) {
      containers = Array.from<HTMLElement>(window.document.querySelectorAll('.vdpview'));
    } else {
      throw new Error('pageType is null or is not supported by this platform');
    }

    if (!(containers && containers.length)) {
      throw new PageNotReady('Containers not yet found');
    }
    return containers;
  }
}
